import React, { useCallback } from 'react';
import { isEmpty } from 'lodash';

import { Box, Button, Tooltip, Typography } from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import QueryBuilderRoundedIcon from '@mui/icons-material/QueryBuilderRounded';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';

import {
  CandidateDetailsAction,
  CandidateListingStatusLabel,
  CandidateStatus,
  CandidateStatusMeaning,
  hasPermission,
} from '@constants';
import { useAppSelector } from '@redux/hooks';
import { selectPermissions } from '@redux/accounts/accounts.selectors';
import { IPositionCandidate } from '@types';
import {
  bookedSlotAt,
  formatDate,
  isInterviewOverdue,
  normalizeCandidateStatus,
} from '@utils';
import { buttonSizeSx, cardButtonSx, secondaryButtonSx } from 'theme/buttons';
import { VerdictMark } from 'components/common/Verdict/Verdict';

const ACTION_SX = buttonSizeSx.sm;

const SECONDARY_ACTION_SX = { ...ACTION_SX, ...secondaryButtonSx } as const;

const CARD_SECONDARY_ACTION_SX = {
  ...cardButtonSx,
  ...secondaryButtonSx,
  borderColor: 'text.secondary',
  '&:hover': { ...secondaryButtonSx['&:hover'], borderColor: 'text.secondary' },
} as const;

const STATUS_LABEL_PROPS = { variant: 'body2', fontWeight: 500 } as const;

const STATUS_META_PROPS = {
  variant: 'caption',
  fontWeight: 400,
  lineHeight: 1.43,
} as const;

const SETTLED_STATUSES = [
  CandidateStatus.CallPassed,
  CandidateStatus.HiringApproved,
  CandidateStatus.Hired,
];

const ClosedStatus: React.FC<{
  label: string;
  hint: string;
  reason?: string | null;
  settled?: boolean;
}> = ({ label, hint, reason, settled }) => (
  <Tooltip title={hint} placement="top">
    <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
      {settled && (
        <Box
          display="flex"
          flexShrink={0}
          color="highlight.scoreGreenStrong"
          aria-hidden="true"
        >
          <VerdictMark passed size={16} />
        </Box>
      )}
      <Box display="flex" flexDirection="column" minWidth={0}>
        <Typography
          {...STATUS_LABEL_PROPS}
          color={settled ? 'text.primary' : 'text.secondary'}
          noWrap
        >
          {label}
        </Typography>
        {Boolean(reason) && (
          <Typography {...STATUS_META_PROPS} color="text.lightGrey" noWrap>
            {reason}
          </Typography>
        )}
      </Box>
    </Box>
  </Tooltip>
);

const statusHint = (
  candidate: IPositionCandidate,
  status: CandidateStatus,
): string => {
  const updated = formatDate(candidate.updatedAt);
  const changed = updated ? `Updated ${updated}` : '';

  return [CandidateStatusMeaning[status], changed].filter(Boolean).join(' · ');
};

const FeedbackTally: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? (
    <Tooltip
      title={`${count} interview ${count === 1 ? 'write-up' : 'write-ups'} on file`}
      placement="top"
    >
      <Box
        display="flex"
        alignItems="center"
        gap={0.25}
        color="text.secondary"
        flexShrink={0}
      >
        <ChatOutlinedIcon sx={{ fontSize: '0.875rem' }} />
        <Typography variant="body2" color="text.secondary">
          {count}
        </Typography>
      </Box>
    </Tooltip>
  ) : null;

export const CandidateListAction: React.FC<{
  candidate: IPositionCandidate;
  onAction: (candidateId: string, action: CandidateDetailsAction) => void;
  inCard?: boolean;
}> = ({ candidate, onAction, inCard }) => {
  const candidateStatus = normalizeCandidateStatus(candidate.status);
  const canUpdateCandidate = hasPermission(
    useAppSelector(selectPermissions),
    'H:W',
  );

  const openWithAction = useCallback(
    (action: CandidateDetailsAction) => (event: React.MouseEvent) => {
      event.stopPropagation();
      onAction(candidate.id, action);
    },
    [candidate.id, onAction],
  );

  const actionTitle = (hint: string) =>
    canUpdateCandidate ? hint : `${hint} — your access is read-only`;

  const statusLabel = candidateStatus
    ? CandidateListingStatusLabel[candidateStatus]
    : candidate.status;

  const tally = <FeedbackTally count={candidate.interviewFeedbackCount ?? 0} />;

  const actionSx = inCard ? cardButtonSx : ACTION_SX;
  const secondaryActionSx = inCard
    ? CARD_SECONDARY_ACTION_SX
    : SECONDARY_ACTION_SX;
  const skipIcon = inCard ? undefined : <ThumbDownOutlinedIcon />;
  const actionGap = inCard ? 0.5 : 0.75;

  if (!candidateStatus)
    return (
      <ClosedStatus
        label={statusLabel}
        hint={statusLabel}
        reason={candidate.closingReason}
      />
    );

  switch (candidateStatus) {
    case CandidateStatus.Submitted:
      return (
        <Box display="flex" alignItems="center" gap={actionGap}>
          <Tooltip
            title={actionTitle(
              'Books a slot. AgileEngine confirms it with the candidate',
            )}
            placement="top"
          >
            <Box component="span" display="inline-flex">
              <Button
                size="small"
                variant="contained"
                disabled={!canUpdateCandidate}
                startIcon={<EventOutlinedIcon />}
                sx={actionSx}
                onClick={openWithAction(
                  CandidateDetailsAction.SCHEDULE_INTERVIEW,
                )}
              >
                Schedule interview
              </Button>
            </Box>
          </Tooltip>
          <Tooltip title={actionTitle('Skip candidate')} placement="top">
            <Box component="span" display="inline-flex">
              <Button
                size="small"
                variant="outlined"
                disabled={!canUpdateCandidate}
                startIcon={skipIcon}
                sx={secondaryActionSx}
                onClick={openWithAction(CandidateDetailsAction.SKIP_CANDIDATE)}
              >
                Skip
              </Button>
            </Box>
          </Tooltip>
          {tally}
        </Box>
      );

    case CandidateStatus.CallScheduled: {
      const booked = isEmpty(candidate.scheduledSlot)
        ? null
        : bookedSlotAt(candidate.scheduledSlot, candidate.clientTimezone);

      if (
        isInterviewOverdue(
          candidate.status,
          candidate.scheduledSlot,
          candidate.clientTimezone,
        )
      )
        return (
          <Box display="flex" alignItems="center" gap={actionGap}>
            <Tooltip
              title={actionTitle(
                booked
                  ? `The slot on ${booked.format('MMM DD, hh:mm a')} has passed. AgileEngine confirms the new time`
                  : 'Moves the booked slot. AgileEngine confirms the new time',
              )}
              placement="top"
            >
              <Box component="span" display="inline-flex">
                <Button
                  size="small"
                  variant="contained"
                  disabled={!canUpdateCandidate}
                  startIcon={<EventRepeatOutlinedIcon />}
                  sx={actionSx}
                  onClick={openWithAction(
                    CandidateDetailsAction.RESCHEDULE_INTERVIEW,
                  )}
                >
                  Reschedule interview
                </Button>
              </Box>
            </Tooltip>
            {tally}
          </Box>
        );

      return (
        <Tooltip
          title={
            booked
              ? `Interview booked for ${booked.format('ddd, MMM DD, hh:mm a')} · ${CandidateStatusMeaning[candidateStatus]}`
              : statusHint(candidate, candidateStatus)
          }
          placement="top"
        >
          <Box
            display="flex"
            alignItems="center"
            gap={0.75}
            color="text.secondary"
            minWidth={0}
          >
            <QueryBuilderRoundedIcon sx={{ fontSize: '1rem', flexShrink: 0 }} />
            <Typography {...STATUS_LABEL_PROPS} color="text.secondary" noWrap>
              {booked ? booked.format('MMM DD, hh:mm a') : statusLabel}
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    case CandidateStatus.InterviewDone:
      return (
        <Box display="flex" alignItems="center" gap={actionGap}>
          <Tooltip title={actionTitle('Proceed to hiring')} placement="top">
            <Box component="span" display="inline-flex">
              <Button
                size="small"
                variant={inCard ? 'contained' : 'outlined'}
                disabled={!canUpdateCandidate}
                startIcon={inCard ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                sx={inCard ? cardButtonSx : SECONDARY_ACTION_SX}
                onClick={openWithAction(
                  CandidateDetailsAction.CLIENT_INTERVIEW_PASSED,
                )}
              >
                Hire
              </Button>
            </Box>
          </Tooltip>
          <Tooltip title={actionTitle('Skip candidate')} placement="top">
            <Box component="span" display="inline-flex">
              <Button
                size="small"
                variant="outlined"
                disabled={!canUpdateCandidate}
                startIcon={skipIcon}
                sx={secondaryActionSx}
                onClick={openWithAction(
                  CandidateDetailsAction.CLIENT_INTERVIEW_FAILED,
                )}
              >
                Skip
              </Button>
            </Box>
          </Tooltip>
          {tally}
        </Box>
      );

    case CandidateStatus.CallCancelled:
      return (
        <Box display="flex" alignItems="center" gap={actionGap}>
          <Tooltip
            title={actionTitle(
              'Rebooking puts the candidate back in the pipeline. AgileEngine confirms the new time',
            )}
            placement="top"
          >
            <Box component="span" display="inline-flex">
              <Button
                size="small"
                variant="contained"
                disabled={!canUpdateCandidate}
                startIcon={<EventRepeatOutlinedIcon />}
                sx={actionSx}
                onClick={openWithAction(
                  CandidateDetailsAction.RESCHEDULE_INTERVIEW,
                )}
              >
                Reschedule interview
              </Button>
            </Box>
          </Tooltip>
          {tally}
        </Box>
      );

    case CandidateStatus.CallRequested:
      return (
        <Tooltip title={statusHint(candidate, candidateStatus)} placement="top">
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
            <QueryBuilderRoundedIcon
              sx={{ fontSize: '1rem', flexShrink: 0, color: 'text.secondary' }}
            />
            <Typography {...STATUS_LABEL_PROPS} color="text.secondary" noWrap>
              {statusLabel}
            </Typography>
          </Box>
        </Tooltip>
      );

    default:
      return (
        <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
          <ClosedStatus
            label={statusLabel}
            hint={statusHint(candidate, candidateStatus)}
            reason={candidate.closingReason}
            settled={SETTLED_STATUSES.includes(candidateStatus)}
          />
          {tally}
        </Box>
      );
  }
};
